/* eslint-disable react-hooks/exhaustive-deps */
import {useCallback, useContext, useEffect, useMemo, useState} from "react"
import {BaseButton} from "components"
import {BaseNewFilter, ConfirmPopup, KlassappTable, KlassappTableHeader} from "uiKit"
import styles from "./ListJobs.module.css"
import {Col, Row} from "antd"
import {KlassappTableHOC} from "HOC"
import {handleError, toastSuccess} from "helpers"
import {careerService} from "services"
import {useHistory} from "react-router-dom"
import {KlassDropAsyncPaginate, KlassDropdown} from "components/Select"
import debounce from "debounce-promise"
import {FormLabel} from "components/Form"
import {CareerServices, JOB_STATUS_LABELS} from "types/careerServices"
import {ActiveJobContext} from "context/ActiveJobContext"
import {useModel, useVisible} from "hooks"
import {ReactComponent as LikeIcon} from "./assets/like.svg"
import {ReactComponent as FilledLikeIcon} from "./assets/like-filled.svg"
import {ReactComponent as DislikeIcon} from "./assets/dislike.svg"
import {ReactComponent as FilledDislikeIcon} from "./assets/dislike-filled.svg"

const EMPLOYMENT_TYPE = {
    "full-time": "Full time",
    "part-time": "Part time",
    contract: "Contract"
}

const EXPERIENCE_LEVEL = {
    "post-grad": "Post-Graduation"
}

const WORKING_TYPE = {
    remote: "Yes",
    hybrid: "Hybrid",
    office: "No"
}

export function Jobs(props) {
    const history = useHistory()
    const {studentId, employerProfileId, onlyPublic = false} = props
    const {
        data,
        page,
        total,
        pageSize,
        columns,
        fields,
        allFields,
        isLoading,
        dispatch,
        getCurrentData,
        menuActions,
        isShowTableHeaderAction,
        tableHeaderActions
    } = props
    const [filter, setFilter] = useState({companies: [], workingTypes: [], employmentTypes: [], expLevels: []})
    const [search, setSearch] = useState("")
    const {changeActiveJob} = useContext(ActiveJobContext)
    const model = useModel()
    const isStudent = model.isStudent()
    const [selectedItem, setSelectedItem] = useState<CareerServices.Job | null>(null)
    const deleteConfirmPopup = useVisible()

    useEffect(() => {
        props.dispatch({isClassComponent: false})
        props.dispatchFunc([
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getMenuActions", func: getMenuActions},
            {key: "getColumns", func: getColumns},
            {key: "onClickDeleteMulti", func: onClickDeleteMulti},
            {key: "getTableHeaderActions", func: getTableHeaderActions},
            {key: "getPageTitle", func: getPageTitle}
        ])
    }, [model.isStudent()])

    useEffect(() => {
        getData()
    }, [search, page, pageSize, studentId, employerProfileId])

    const getPageTitle = () => {
        return "Jobs"
    }

    const canShowActionIcon = () => {
        return model.isStaffOrAdmin()
    }

    const getTableHeaderActions = () => {
        const {t, onClickShowConfirmModal} = props
        return model.isStaffOrAdmin()
            ? [
                  {
                      title: t("common:action.delete"),
                      icon: "DELETE",
                      action: () => onClickShowConfirmModal("DELETE")
                  }
              ]
            : []
    }

    const onClickDelete = (deletedItem) => {
        setSelectedItem(deletedItem)
        deleteConfirmPopup.open()
    }

    const onClickDeleteMulti = async () => {
        const checkedItems = getCurrentData().filter((item) => item.isChecked)
        const payload = checkedItems.map((item) => item.id)
        try {
            dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await careerService.deleteJobs({ids: payload})
            toastSuccess("Jobs deleted successfully")
            await getData()
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
        }
    }

    function getMenuActions() {
        const actions = []
        if (canShowActionIcon()) {
            actions.push({
                title: "Remove",
                icon: "DELETE",
                action: onClickDelete,
                canShow: canShowActionIcon
            })
        }
        return actions
    }

    async function getData() {
        try {
            dispatch({isLoading: true})
            let filterParams: any = {}
            if (search) {
                filterParams.search = search
            }
            if (filter.companies && filter.companies.length > 0) {
                const companyIds = filter.companies.map((x) => x.id)
                filterParams.companyIds = companyIds
            }
            if (filter.employmentTypes && filter.employmentTypes.length > 0) {
                const employmentTypes = filter.employmentTypes.map((x) => x.value)
                filterParams.employmentTypes = employmentTypes
            }
            if (filter.expLevels && filter.expLevels.length > 0) {
                const expLevels = filter.expLevels.map((x) => x.value)
                filterParams.expLevels = expLevels
            }
            if (filter.workingTypes && filter.workingTypes.length > 0) {
                const workingTypes = filter.workingTypes.map((x) => x.value)
                filterParams.workingTypes = workingTypes
            }
            if (studentId) {
                filterParams.studentId = studentId
            }
            if (employerProfileId) {
                filterParams.employerProfileId = employerProfileId
            }
            let statuses = [
                CareerServices.JobStatus.Public,
                CareerServices.JobStatus.PendingApproval,
                CareerServices.JobStatus.Draft
            ]
            if (onlyPublic) {
                statuses = [CareerServices.JobStatus.Public]
            }
            filterParams.statuses = statuses
            const {data, total} = await careerService.getListJobs({
                range: {page, pageSize},
                filter: filterParams
            })
            const newData = data.map((item) => {
                return {
                    ...item,
                    employerName: item.employer.companyName,
                    employmentTypeHtml: EMPLOYMENT_TYPE[item.employmentType],
                    statusHtml: JOB_STATUS_LABELS[item.status],
                    reactionHtml: renderReaction(item)
                }
            })
            dispatch({data: newData, total})
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const getEmployers = async (search = "", loadedOptions) => {
        try {
            const pageSize = 20
            const page = Math.ceil(loadedOptions.length / pageSize) + 1
            const params: any = {
                range: {
                    pageSize,
                    page
                }
            }
            if (search) {
                params.filter.search = search
            }
            const {data, total} = await careerService.getListEmployers(params)
            const employers = data.map((item) => {
                const _item: any = {...item}
                _item.name = item.companyName
                return _item
            })
            return {
                options: employers,
                hasMore: loadedOptions.length < total
            }
        } catch (e) {
            return {options: [], hasMore: false}
        }
    }

    let employmentTypes: any = []
    Object.entries(EMPLOYMENT_TYPE).forEach(([key, value]) => {
        employmentTypes.push({
            value: key,
            label: value
        })
    })

    let workingTypes: any = []
    Object.entries(WORKING_TYPE).forEach(([key, value]) => {
        workingTypes.push({
            value: key,
            label: value
        })
    })

    let expLevels: any = []
    Object.entries(EXPERIENCE_LEVEL).forEach(([key, value]) => {
        expLevels.push({
            value: key,
            label: value
        })
    })

    const navigate_to = (_id?: string) => {
        let id = _id
        if (_id === undefined) {
            history.push(`/career-services/jobs/create`)
        } else {
            history.push(`/career-services/job-details/${id}`)
        }
    }

    const onClickClearFilter = () => {
        setFilter({companies: [], workingTypes: [], employmentTypes: [], expLevels: []})
    }

    const onChangeFilter = (key, value) => {
        const newFilter = {...filter}
        newFilter[key] = value
        setFilter(newFilter)
    }

    const onSearchInput = (search) => {
        setSearch(search)
    }

    const onApplyFilter = () => {
        getData()
    }

    function getColumns() {
        const cols = [
            {
                title: "ID",
                field: "code",
                headerStyle: {minWidth: "100px"}
            },
            {
                title: "Title",
                field: "title",
                headerStyle: {minWidth: "100px"}
            },
            {
                title: "Employer",
                field: "employerName",
                headerStyle: {minWidth: "100px"}
            },
            {
                title: "Employment Type",
                field: "employmentTypeHtml",
                headerStyle: {minWidth: "150px"}
            },
            {
                title: "Status",
                field: "statusHtml",
                headerStyle: {minWidth: "150px"}
            }
        ]
        if (isStudent) {
            cols.push({
                title: "Reaction",
                field: "reactionHtml",
                headerStyle: {minWidth: "150px"}
            })
        }
        return cols
    }

    function getFields() {
        const arr = ["ID", "Title", "Employer", "Employment Type", "Status"]
        if (isStudent) arr.push("Reaction")
        return arr
    }

    const onClickRowItem = (row) => {
        changeActiveJob(row)
    }

    const debounceEmployer = useCallback(debounce(getEmployers, 300), [])

    const isEmployer = useMemo(() => {
        return !!employerProfileId
    }, [employerProfileId])

    const handleDeleteJob = async () => {
        try {
            await careerService.deleteJobs({ids: [selectedItem.id]})
            toastSuccess("Job deleted successfully")
            getData()
        } catch (error) {
            handleError(error)
        } finally {
            deleteConfirmPopup.close()
        }
    }

    const handleReaction = async (e, type: CareerServices.ReactionType, job: CareerServices.Job) => {
        try {
            e.preventDefault()
            e.stopPropagation()
            if (
                (type === CareerServices.ReactionType.Like && job.isLiked) ||
                (type === CareerServices.ReactionType.Dislike && job.isDisliked)
            )
                return
            const payload = {
                jobId: job.id,
                reactionType: type
            }
            const _reactionStat = await careerService.reactJob(payload)
            if (_reactionStat) {
                dispatch({
                    data: getCurrentData().map((item) =>
                        item.id === job.id
                            ? {...item, ..._reactionStat, reactionHtml: renderReaction({...item, ..._reactionStat})}
                            : item
                    )
                })
            }
        } catch (error) {
            handleError(error)
        }
    }

    const renderReaction = (job: CareerServices.Job) => {
        return (
            <div className={styles.reactionsWrap}>
                <div
                    className={styles.reactionContainer}
                    onClick={(e) => handleReaction(e, CareerServices.ReactionType.Like, job)}>
                    {job.isLiked ? <FilledLikeIcon width={20} height={20} /> : <LikeIcon width={20} height={20} />}
                    <div className={styles.count}>({job.totalLikes})</div>
                </div>
                <div
                    className={styles.reactionContainer}
                    onClick={(e) => handleReaction(e, CareerServices.ReactionType.Dislike, job)}>
                    {job.isDisliked ? (
                        <FilledDislikeIcon width={20} height={20} />
                    ) : (
                        <DislikeIcon width={20} height={20} />
                    )}
                    <div className={styles.count}>({job.totalDislikes})</div>
                </div>
            </div>
        )
    }

    return (
        <div>
            <BaseNewFilter
                filter={filter}
                onClick={onApplyFilter}
                onClickClear={onClickClearFilter}
                onSearchInput={onSearchInput}
                renderRightFilter={() => (
                    <>
                        {isStudent ? null : (
                            <div className={styles.actionWrap}>
                                <BaseButton title="Add" onClick={() => navigate_to()} className={styles.addButton} />
                            </div>
                        )}
                    </>
                )}>
                <Row gutter={[12, 12]}>
                    {!isEmployer && (
                        <Col span={12}>
                            <div>
                                <FormLabel label="Company" />
                                <KlassDropAsyncPaginate
                                    value={filter.companies}
                                    loadOptions={debounceEmployer}
                                    onChange={(newValue) => onChangeFilter("companies", newValue)}
                                    placeholder="Company"
                                    isMulti
                                />
                            </div>
                        </Col>
                    )}
                    <Col span={12}>
                        <div>
                            <FormLabel label="Remote" />
                            <KlassDropdown
                                value={filter.workingTypes}
                                placeholder="Select"
                                onChange={(option: any) => onChangeFilter("workingTypes", option)}
                                valueKey="value"
                                labelKey="label"
                                options={workingTypes}
                                hasEmptyOption
                                isMulti
                            />
                        </div>
                    </Col>
                    <Col span={12}>
                        <div>
                            <FormLabel label="Employment Type" />
                            <KlassDropdown
                                value={filter.employmentTypes}
                                placeholder="Select"
                                onChange={(option: any) => onChangeFilter("employmentTypes", option)}
                                valueKey="value"
                                labelKey="label"
                                options={employmentTypes}
                                hasEmptyOption
                                isMulti
                            />
                        </div>
                    </Col>
                    <Col span={12}>
                        <div>
                            <FormLabel label="Experience Level" />
                            <KlassDropdown
                                value={filter.expLevels}
                                placeholder="Select"
                                onChange={(option: any) => onChangeFilter("expLevels", option)}
                                valueKey="value"
                                labelKey="label"
                                options={expLevels}
                                hasEmptyOption
                                isMulti
                            />
                        </div>
                    </Col>
                </Row>
            </BaseNewFilter>
            <Row className={styles.marginTop}>
                <Col span={24}>
                    <KlassappTableHeader
                        isShowAction={isShowTableHeaderAction}
                        actions={tableHeaderActions}
                        page={page}
                        total={total}
                        defaultPageSize={pageSize}
                        onChangePage={props.onChangePage}
                        onChangeRowPerPage={props.onChangeRowPerPage}
                    />
                    <KlassappTable
                        columns={columns}
                        menuActions={menuActions}
                        data={data}
                        isLoading={isLoading}
                        fields={fields}
                        allFields={allFields}
                        isShowCheckedColumn={canShowActionIcon()}
                        onChangeFields={props.onChangeFields}
                        onUpdateRowData={props.onUpdateRowData}
                        onUpdateTableData={props.onUpdateTableData}
                        onClickRowItem={onClickRowItem}
                    />
                </Col>
            </Row>
            <ConfirmPopup
                title={`Delete Job ${selectedItem?.title}`}
                isVisible={deleteConfirmPopup.isVisible}
                description="Are you sure you want to delete this job?"
                onClose={deleteConfirmPopup.close}
                onConfirm={handleDeleteJob}
            />
        </div>
    )
}

export const ListJobs = KlassappTableHOC(Jobs)
